import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { AppError } from '../../api/contract';
import { useRepository } from '../../data/RepositoryProvider';
import { useResource } from '../../hooks/useResource';
import { MutationError, toDialogError } from '../partners/PartnerFormDialog';
import { toPageError } from '../partners/PartnerListPage';
import { gamesRepositoryToken } from './repository';
import type { GameDetail, GameTypeListQuery, GameTypeSummary } from './types';
import { normalizeMasterCode } from './types';

interface Values {
  readonly gameTypeId: string;
  readonly code: string;
  readonly name: string;
  readonly reason: string;
}

export function GameDialog({
  game,
  onClose,
  onSaved,
  open,
}: {
  readonly game?: GameDetail | undefined;
  readonly onClose: () => void;
  readonly onSaved: (game: GameDetail) => void;
  readonly open: boolean;
}) {
  const repository = useRepository(gamesRepositoryToken);
  const { t } = useTranslation();
  const guard = useRef(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Pick<AppError, 'message' | 'requestId'> | null>(null);
  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    reset,
    watch,
  } = useForm<Values>({ defaultValues: defaults(game) });
  const normalizedCode = normalizeMasterCode(watch('code'));

  useEffect(() => {
    if (!open) return;
    reset(defaults(game));
    setError(null);
    setBusy(false);
    guard.current = false;
  }, [game, open, reset]);

  const submit = handleSubmit(async (values) => {
    if (guard.current) return;
    guard.current = true;
    setBusy(true);
    setError(null);
    let saved = false;
    try {
      const response =
        game === undefined
          ? await repository.createGame({
              gameTypeId: values.gameTypeId,
              code: normalizeMasterCode(values.code),
              name: values.name.trim(),
              reason: values.reason.trim(),
            })
          : await repository.updateGame(game.id, {
              name: values.name.trim(),
              reason: values.reason.trim(),
            });
      saved = true;
      onSaved(response.data);
      onClose();
    } catch (submitError) {
      setError(toDialogError(submitError, t('games.errors.mutation')));
    } finally {
      if (!saved) guard.current = false;
      setBusy(false);
    }
  });

  return (
    <Dialog fullWidth maxWidth="sm" onClose={() => !guard.current && onClose()} open={open}>
      <DialogTitle>
        {t(game === undefined ? 'games.forms.gameCreate' : 'games.forms.gameEdit')}
      </DialogTitle>
      <DialogContent>
        <Stack
          component="form"
          id="game-form"
          noValidate
          onSubmit={submit}
          spacing={2}
          sx={{ pt: 1 }}
        >
          {error === null ? null : <MutationError error={error} />}
          {game === undefined ? (
            open ? (
              <Controller
                control={control}
                name="gameTypeId"
                render={({ field }) => (
                  <GameTypeSelector
                    disabled={busy}
                    error={errors.gameTypeId?.message}
                    onChange={field.onChange}
                    value={field.value}
                  />
                )}
                rules={{ required: t('games.validation.gameTypeRequired') }}
              />
            ) : null
          ) : (
            <TextField
              disabled
              label={t('games.fields.gameType')}
              value={`${game.gameType.code} · ${game.gameType.name}`}
            />
          )}
          <TextField
            disabled={busy || game !== undefined}
            error={errors.code !== undefined}
            helperText={errors.code?.message}
            label={t('games.fields.gameCode')}
            required
            {...register('code', {
              validate: (value) =>
                /^[A-Z0-9][A-Z0-9_-]{1,63}$/u.test(normalizeMasterCode(value)) ||
                t('games.validation.gameCode'),
            })}
          />
          {game === undefined && normalizedCode.length > 0 ? (
            <Alert severity="info">
              <Typography variant="caption">{t('games.forms.normalizedCode')}</Typography>
              <Typography component="p" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
                {normalizedCode}
              </Typography>
            </Alert>
          ) : null}
          <TextField
            autoFocus={game !== undefined}
            disabled={busy}
            error={errors.name !== undefined}
            helperText={errors.name?.message}
            label={t('games.fields.gameName')}
            required
            {...register('name', {
              maxLength: { value: 200, message: t('games.validation.nameMaximum') },
              validate: (value) => value.trim().length > 0 || t('games.validation.nameRequired'),
            })}
          />
          <TextField
            disabled={busy}
            error={errors.reason !== undefined}
            helperText={errors.reason?.message}
            label={t('games.fields.reason')}
            minRows={2}
            multiline
            required
            {...register('reason', {
              maxLength: { value: 2000, message: t('games.validation.reasonMaximum') },
              validate: (value) => value.trim().length > 0 || t('games.validation.reasonRequired'),
            })}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={onClose}>
          {t('actions.cancel')}
        </Button>
        <Button form="game-form" loading={busy} type="submit" variant="contained">
          {t(game === undefined ? 'actions.create' : 'actions.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function GameTypeSelector({
  disabled,
  error,
  onChange,
  value,
}: {
  readonly disabled: boolean;
  readonly error?: string | undefined;
  readonly onChange: (value: string) => void;
  readonly value: string;
}) {
  const repository = useRepository(gamesRepositoryToken);
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<GameTypeSummary | null>(null);
  const [query, setQuery] = useState<GameTypeListQuery>(gameTypeSelectorQuery);
  const resource = useResource(
    (signal) => repository.listGameTypes(query, signal),
    [query, repository],
    () => false,
  );
  const activeItems = (resource.data?.items ?? []).filter((item) => item.status === 'ACTIVE');
  const options =
    selected !== null && !activeItems.some((item) => item.id === selected.id)
      ? [selected, ...activeItems]
      : activeItems;
  const total = resource.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / query.pageSize));

  return (
    <Stack spacing={1}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        <TextField
          disabled={disabled}
          fullWidth
          label={t('games.filters.gameTypeSearch')}
          onChange={(event) => setSearch(event.target.value)}
          value={search}
        />
        <Button
          disabled={disabled}
          onClick={() =>
            setQuery((current) => ({
              ...current,
              page: 1,
              filters: { code: normalizeMasterCode(search), name: '', status: 'ACTIVE' },
            }))
          }
          type="button"
          variant="outlined"
        >
          {t('games.actions.searchGameTypes')}
        </Button>
      </Stack>
      {resource.status === 'error' ? (
        <SelectorError error={toPageError(resource.error, t('games.errors.loadGameTypes'))} />
      ) : null}
      <TextField
        disabled={disabled || resource.status === 'loading' || resource.status === 'error'}
        error={error !== undefined}
        helperText={error}
        label={t('games.fields.gameType')}
        onChange={(event) => {
          const id = event.target.value;
          setSelected(activeItems.find((item) => item.id === id) ?? selected);
          onChange(id);
        }}
        required
        select
        slotProps={{ select: { native: true } }}
        value={value}
      >
        <option value="">{t('games.forms.selectGameType')}</option>
        {options.map((item) => (
          <option key={item.id} value={item.id}>
            {item.code} · {item.name}
          </option>
        ))}
      </TextField>
      {resource.data === null ? null : (
        <Stack
          direction="row"
          spacing={1}
          sx={{ alignItems: 'center', justifyContent: 'space-between' }}
        >
          <Typography color="text.secondary" variant="caption">
            {t('games.forms.gameTypeResults', {
              shown: activeItems.length,
              total,
              page: query.page,
              pages,
            })}
          </Typography>
          <Box>
            <Button
              disabled={disabled || query.page <= 1}
              onClick={() => setQuery((current) => ({ ...current, page: current.page - 1 }))}
              size="small"
              type="button"
            >
              {t('games.actions.previousGameTypes')}
            </Button>
            <Button
              disabled={disabled || query.page >= pages}
              onClick={() => setQuery((current) => ({ ...current, page: current.page + 1 }))}
              size="small"
              type="button"
            >
              {t('games.actions.nextGameTypes')}
            </Button>
          </Box>
        </Stack>
      )}
    </Stack>
  );
}

function SelectorError({
  error,
}: {
  readonly error: Pick<AppError, 'message' | 'requestId'> | null;
}) {
  if (error === null) return null;
  return (
    <Alert severity="error">
      {error.message}
      {error.requestId === null ? null : ` Request ID: ${error.requestId}`}
    </Alert>
  );
}

const gameTypeSelectorQuery = Object.freeze<GameTypeListQuery>({
  page: 1,
  pageSize: 25,
  sort: 'code',
  order: 'asc',
  filters: { code: '', name: '', status: 'ACTIVE' },
});

function defaults(game: GameDetail | undefined): Values {
  return {
    gameTypeId: game?.gameType.id ?? '',
    code: game?.code ?? '',
    name: game?.name ?? '',
    reason: '',
  };
}
